import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../api/supabase';

/**
 * クエリ文字列でサーバー側フィルタリングし、検索ターゲット一覧を返す。
 * query が空の場合はフェッチしない（enabled: false）。
 *
 * 返却される各ターゲットの shape:
 *   Group:    { type: 'group',    id, name, startYear, members: [{ id, name }] }
 *   Comedian: { type: 'comedian', id, name, startYear, agency, groups: [{ id, name }] }
 */
export const useSearchTargets = (query) => {
  const trimmed = (query ?? '').trim();

  return useQuery({
    queryKey: ['searchTargets', trimmed],
    enabled: trimmed.length > 0,
    queryFn: async () => {
      const [
        { data: comedians, error: cError },
        { data: groups, error: gError },
        { data: memberships, error: mError },
      ] = await Promise.all([
        supabase
          .from('comedians')
          .select('*')
          .eq('status', 'active')
          .or(`name.ilike.%${trimmed}%,name_kana.ilike.%${trimmed}%`),
        supabase
          .from('groups')
          .select('*')
          .eq('status', 'active')
          .or(`name.ilike.%${trimmed}%,name_kana.ilike.%${trimmed}%`),
        supabase.from('comedian_group').select('comedian_id, group_id'),
      ]);

      if (cError) throw cError;
      if (gError) throw gError;
      if (mError) throw mError;

      // グループメンバーとして必要な追加芸人IDを収集
      const matchedComedianIds = new Set(comedians.map((c) => c.id));
      const matchedGroupIds = groups.map((g) => g.id);

      const memberComedianIds = (memberships ?? [])
        .filter((m) => matchedGroupIds.includes(m.group_id) && !matchedComedianIds.has(m.comedian_id))
        .map((m) => m.comedian_id);

      // グループメンバー表示用の追加芸人データをフェッチ
      const extraComedians =
        memberComedianIds.length > 0
          ? await supabase
              .from('comedians')
              .select('*')
              .in('id', [...new Set(memberComedianIds)])
              .then(({ data }) => data ?? [])
          : [];

      // マップ構築（検索結果 + JOIN用追加データ）
      const comedianMap = Object.fromEntries(
        [...comedians, ...extraComedians].map((c) => [c.id, c])
      );
      const groupMap = Object.fromEntries(groups.map((g) => [g.id, g]));

      const groupMemberMap = {};   // group_id → comedian_id[]
      const comedianGroupMap = {}; // comedian_id → group_id[]
      (memberships ?? []).forEach(({ comedian_id, group_id }) => {
        if (!groupMemberMap[group_id]) groupMemberMap[group_id] = [];
        groupMemberMap[group_id].push(comedian_id);
        if (!comedianGroupMap[comedian_id]) comedianGroupMap[comedian_id] = [];
        comedianGroupMap[comedian_id].push(group_id);
      });

      // グループターゲット（検索にマッチしたグループのみ）
      const groupTargets = groups.map((g) => ({
        type: 'group',
        id: `group-${g.id}`,
        name: g.name,
        name_kana: g.name_kana ?? '',
        startYear: g.formed_year,
        members: (groupMemberMap[g.id] ?? [])
          .map((cId) => comedianMap[cId])
          .filter(Boolean)
          .map(({ id, name, name_kana }) => ({ id, name, name_kana: name_kana ?? '' })),
      }));

      // 芸人ターゲット（検索にマッチした芸人）
      const comedianTargets = comedians.map((c) => ({
        type: 'comedian',
        id: `comedian-${c.id}`,
        name: c.name,
        name_kana: c.name_kana ?? '',
        startYear: c.debut_year,
        agency: c.agency,
        groups: (comedianGroupMap[c.id] ?? [])
          .map((gId) => groupMap[gId])
          .filter(Boolean)
          .map(({ id, name, name_kana, formed_year }) => ({ id, name, name_kana: name_kana ?? '', formed_year })),
      }));

      // グループメンバー用芸人ターゲット（メンバークリック時の選択に必要）
      const memberComedianTargets = extraComedians.map((c) => ({
        type: 'comedian',
        id: `comedian-${c.id}`,
        name: c.name,
        name_kana: c.name_kana ?? '',
        startYear: c.debut_year,
        agency: c.agency,
        groups: (comedianGroupMap[c.id] ?? [])
          .map((gId) => groupMap[gId])
          .filter(Boolean)
          .map(({ id, name, name_kana, formed_year }) => ({ id, name, name_kana: name_kana ?? '', formed_year })),
      }));

      return [...groupTargets, ...comedianTargets, ...memberComedianTargets];
    },
  });
};
